import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Calendar, User, Info, CheckSquare, Square, Users } from "lucide-react";
import { format } from "date-fns";
import { useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EditTaskModal } from "./EditTaskModal";
import { useDeleteTask, useUpdateTask } from "@/hooks/tasks/useTasks";
import { TaskInfoPopover } from "./TaskInfoPopover";
import { TaskLinkedUsersDialog } from "./TaskLinkedUsersDialog";
import { truncateHtmlContent } from "@/utils/htmlUtils";

interface TaskCardProps {
  task: any;
  onTaskUpdated?: () => void;
}

export const TaskCard = ({ task, onTaskUpdated }: TaskCardProps) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showLinkedUsers, setShowLinkedUsers] = useState(false);
  const deleteTaskMutation = useDeleteTask();
  const updateTaskMutation = useUpdateTask();

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      todo: { variant: "secondary" as const, label: t("tasks.status.todo", "To Do") },
      inProgress: { variant: "default" as const, label: t("tasks.status.inProgress", "In Progress") },
      done: { variant: "success" as const, label: t("tasks.status.done", "Done") },
      cancelled: { variant: "destructive" as const, label: t("tasks.status.cancelled", "Cancelled") }
    };
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.todo;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: "secondary" as const, label: t("tasks.priority.low", "Low") },
      medium: { variant: "default" as const, label: t("tasks.priority.medium", "Medium") },
      high: { variant: "warning" as const, label: t("tasks.priority.high", "High") },
      urgent: { variant: "destructive" as const, label: t("tasks.priority.urgent", "Urgent") }
    };
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleEdit = () => {
    // Just update URL - let parent component handle the modal
    const currentParams = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...currentParams, task: task.id }, { replace: false });
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteTaskMutation.mutateAsync(task.id);
      setIsDeleting(false);
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error("Failed to delete task:", error);
    }
  };

  // No longer needed - parent handles modal

  const handleToggleStatus = async () => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const newStatus = task.status === 'done' ? 'todo' : 'done';
      
      await updateTaskMutation.mutateAsync({
        id: task.id,
        status: newStatus
      });

      // Update the task object locally
      task.status = newStatus;
      
      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <button
              onClick={handleToggleStatus}
              disabled={isUpdating}
              className={`p-1 rounded transition-colors mt-0.5 flex-shrink-0 ${
                task.status === 'done' 
                  ? 'text-green-600 hover:text-green-700' 
                  : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300'
              } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={task.status === 'done' ? t("tasks.actions.markUndone", "Mark as undone") : t("tasks.actions.markDone", "Mark as done")}
            >
              {task.status === 'done' ? (
                <CheckSquare className="h-4 w-4" />
              ) : (
                <Square className="h-4 w-4" />
              )}
            </button>
            <button
              onClick={handleEdit}
              className={`text-left hover:underline text-lg font-semibold line-clamp-2 ${(task.status === 'done' || task.status === 'cancelled') ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-gray-100'}`}
              title={t("common.edit", "Edit task")}
            >
              {task.name}
            </button>
          </div>
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm"
                  title={t("tasks.info", "Task Information")}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0 max-w-[20rem]" align="end">
                <TaskInfoPopover task={task} onTaskUpdated={onTaskUpdated} />
              </PopoverContent>
            </Popover>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowLinkedUsers(true)}
              title={t("tasks.linkedUsers.manage", "Manage linked users")}
            >
              <Users className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleEdit}
              title={t("common.edit", "Edit")}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={handleDelete}
              title={t("common.delete", "Delete")}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {task.description && (
          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-3">{truncateHtmlContent(task.description, 120)}</p>
        )}
        
        <div className="flex items-center gap-2">
          {getStatusBadge(task.status)}
          {getPriorityBadge(task.priority)}
        </div>
        
        {task.due_date && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(task.due_date), "MMM dd, yyyy")}</span>
          </div>
        )}
        
        {task.assignee && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <User className="h-4 w-4" />
            <span>{t("tasks.list.assignedTo", "Assigned to")}: {task.assignee.first_name} {task.assignee.last_name}</span>
          </div>
        )}
        
        {task.owner && (
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <User className="h-4 w-4" />
            <span>{t("tasks.list.owner", "Owner")}: {task.owner.first_name} {task.owner.last_name}</span>
          </div>
        )}
        
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {t("tasks.list.createdAt", "Created")}: {task.created_at ? format(new Date(task.created_at), "MMM dd, yyyy") : "-"}
        </div>
      </CardContent>
    </Card>

    {/* Linked Users Dialog */}
    <TaskLinkedUsersDialog
      taskId={task.id}
      taskName={task.name}
      isOpen={showLinkedUsers}
      onClose={() => setShowLinkedUsers(false)}
    />

    {/* Delete Confirmation Dialog */}
    <AlertDialog open={isDeleting} onOpenChange={(open) => !open && setIsDeleting(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("common.deleteConfirmation", { count: 1 })}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("common.deleteWarning")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={confirmDelete}
            className="bg-red-600 hover:bg-red-700"
            disabled={deleteTaskMutation.isPending}
          >
            {deleteTaskMutation.isPending ? t("common.deleting", "Deleting...") : t("common.delete")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};
